"use client";

import { useCallback, useEffect, useState } from "react";

interface AmbientVideoProps {
  src: string;
  /** Poster frame shown while the video buffers. Should match whatever
   * underlay image the caller already renders behind this component. */
  poster?: string;
  className?: string;
}

/** Minimal shape of the non-standard Network Information API. Not in lib.dom. */
interface NetworkInformationLike {
  saveData?: boolean;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
}

/**
 * Ambient b-roll video that fades in once its first frame is decoded instead
 * of hard-swapping from a poster. Whatever sits beneath it (underlay image or
 * ink surface) stays visible until the video is actually ready, so navigation
 * never flashes a stale placeholder frame.
 *
 * The <video> element itself is only mounted once the client confirms motion
 * is actually wanted: prefers-reduced-motion is off and the connection is not
 * flagged as data-saver. CSS alone (motion-reduce:hidden etc) still lets the
 * browser fetch the source before paint, so the gate has to stop the element
 * from ever entering the DOM. Reduced-motion / save-data clients render
 * nothing here and rely on the caller's poster image underneath.
 */
export function AmbientVideo({ src, poster, className = "" }: AmbientVideoProps) {
  const [ready, setReady] = useState(false);
  const [wantsMotion, setWantsMotion] = useState(false);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike })
      .connection;

    const evaluate = () => {
      setWantsMotion(!motionQuery.matches && !connection?.saveData);
    };

    evaluate();
    motionQuery.addEventListener("change", evaluate);
    connection?.addEventListener?.("change", evaluate);
    return () => {
      motionQuery.removeEventListener("change", evaluate);
      connection?.removeEventListener?.("change", evaluate);
    };
  }, []);

  const markReady = useCallback(() => setReady(true), []);
  // If the video was cached and decoded before hydration, loadeddata has
  // already fired; check readyState on mount so we don't stay transparent.
  const attach = useCallback((el: HTMLVideoElement | null) => {
    if (el && el.readyState >= 2) setReady(true);
  }, []);

  if (!wantsMotion) return null;

  return (
    <video
      ref={attach}
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      onLoadedData={markReady}
      onPlaying={markReady}
      style={ready ? undefined : { opacity: 0 }}
      className={`transition-opacity duration-500 ease-out ${className}`}
    />
  );
}
